import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ReferenceArea, Brush, Legend,
} from "recharts";
import { Droplets, Save, Send, ArrowLeft, ZoomOut, Search, Download, MapPin } from "lucide-react";
import { toast } from "sonner";

function toLocalInput(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function StationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [station, setStation] = useState(null);
  const [latest, setLatest] = useState({});
  const [channel, setChannel] = useState(0);
  const [readings, setReadings] = useState([]);
  // multi-channel comparison
  const [compareMode, setCompareMode] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [normalize, setNormalize] = useState(false);
  const [multiReadings, setMultiReadings] = useState([]);
  const [msg, setMsg] = useState({ message: "", sms: false });
  const [channels, setChannels] = useState([]);
  const [metaForm, setMetaForm] = useState({ name: "", location: "", lat: "", lng: "" });

  // range
  const [rangeMode, setRangeMode] = useState("preset");
  const [hours, setHours] = useState(24);
  const [fromVal, setFromVal] = useState("");
  const [toVal, setToVal] = useState("");
  const [applied, setApplied] = useState(null); // {from, to}

  // zoom
  const [domain, setDomain] = useState(null);
  const [refLeft, setRefLeft] = useState(null);
  const [refRight, setRefRight] = useState(null);

  // appearance
  const CHART_COLORS = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#ec4899"];
  const [chartColor, setChartColor] = useState("#2563eb");
  const [yMin, setYMin] = useState("");
  const [yMax, setYMax] = useState("");

  const loadStation = useCallback(async () => {
    const [s, l] = await Promise.all([
      api.get(`/stations/${id}`),
      api.get(`/stations/${id}/latest`),
    ]);
    setStation(s.data);
    setChannels(s.data.channels || []);
    setMetaForm({
      name: s.data.name || "", location: s.data.location || "",
      lat: s.data.lat ?? "", lng: s.data.lng ?? "",
    });
    setLatest(l.data);
  }, [id]);

  const loadLatest = useCallback(async () => {
    const [s, l] = await Promise.all([
      api.get(`/stations/${id}`),
      api.get(`/stations/${id}/latest`),
    ]);
    setStation(s.data);
    setLatest(l.data);
  }, [id]);

  const loadReadings = useCallback(async () => {
    let url = `/stations/${id}/readings?channel=${channel}&limit=6000`;
    if (rangeMode === "custom" && applied) {
      url += `&from=${encodeURIComponent(applied.from)}&to=${encodeURIComponent(applied.to)}`;
    } else {
      url += `&hours=${hours}`;
    }
    const { data } = await api.get(url);
    setReadings(data.map((r) => ({ ...r, t: Date.parse(r.timestamp) })));
  }, [id, channel, hours, rangeMode, applied]);

  const loadMultiReadings = useCallback(async () => {
    if (!compareMode || selectedChannels.length === 0) { setMultiReadings([]); return; }
    const suffix = (rangeMode === "custom" && applied)
      ? `&from=${encodeURIComponent(applied.from)}&to=${encodeURIComponent(applied.to)}`
      : `&hours=${hours}`;
    const results = await Promise.all(
      selectedChannels.map((ch) => api.get(`/stations/${id}/readings?channel=${ch}&limit=6000${suffix}`)),
    );
    const map = new Map();
    results.forEach((res, i) => {
      const ch = selectedChannels[i];
      res.data.forEach((r) => {
        const t = Date.parse(r.timestamp);
        if (!map.has(t)) map.set(t, { t });
        map.get(t)[`ch_${ch}`] = r.value;
      });
    });
    setMultiReadings([...map.values()].sort((a, b) => a.t - b.t));
  }, [compareMode, selectedChannels, id, hours, rangeMode, applied]);

  useEffect(() => { loadStation(); }, [loadStation]);
  // Auto-refresh live values (does not touch the config form)
  useEffect(() => {
    const tm = setInterval(loadLatest, 15000);
    return () => clearInterval(tm);
  }, [loadLatest]);
  useEffect(() => {
    loadReadings();
    const tm = setInterval(loadReadings, 15000);
    return () => clearInterval(tm);
  }, [loadReadings]);
  useEffect(() => {
    loadMultiReadings();
    const tm = setInterval(loadMultiReadings, 15000);
    return () => clearInterval(tm);
  }, [loadMultiReadings]);

  // Each chart (station + channel) remembers its assigned color
  useEffect(() => {
    if (!station) return;
    const saved = localStorage.getItem(`dc_color_${station.code}_${channel}`);
    setChartColor(saved || CHART_COLORS[channel % CHART_COLORS.length]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station, channel]);

  const currentCh = channels.find((c) => c.index === channel);

  const chColor = (ch) => CHART_COLORS[ch % CHART_COLORS.length];
  const chInfo = (ch) => channels.find((c) => c.index === ch);

  const toggleCompare = (on) => {
    setCompareMode(on);
    setDomain(null);
    if (on && selectedChannels.length === 0) setSelectedChannels([channel]);
  };

  const toggleChannelSel = (ch) => {
    setSelectedChannels((prev) => prev.includes(ch)
      ? prev.filter((c) => c !== ch)
      : [...prev, ch].sort((a, b) => a - b));
  };

  const compareData = useMemo(() => {
    if (!normalize) return multiReadings;
    const bounds = {};
    selectedChannels.forEach((ch) => {
      const key = `ch_${ch}`;
      let mn = Infinity; let mx = -Infinity;
      multiReadings.forEach((r) => {
        const v = r[key];
        if (v != null) { if (v < mn) mn = v; if (v > mx) mx = v; }
      });
      bounds[key] = { mn, mx };
    });
    return multiReadings.map((r) => {
      const o = { t: r.t };
      selectedChannels.forEach((ch) => {
        const key = `ch_${ch}`;
        const v = r[key];
        if (v != null) {
          const { mn, mx } = bounds[key];
          o[key] = mx > mn ? ((v - mn) / (mx - mn)) * 100 : 50;
        }
      });
      return o;
    });
  }, [multiReadings, selectedChannels, normalize]);

  const canConfig = user?.role === "admin"
    || (user?.role === "avanzado" && (user?.station_access || []).includes(station?.code));

  const fmtTick = useCallback((ts) => {
    const d = new Date(ts);
    const spanBig = rangeMode === "custom" || hours > 24;
    return spanBig
      ? d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString(undefined, { hour: "2-digit" }) + "h"
      : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }, [hours, rangeMode]);
  const fmtFull = (ts) => new Date(ts).toLocaleString();

  const onDown = (e) => { if (e && e.activeLabel != null) setRefLeft(e.activeLabel); };
  const onMove = (e) => { if (refLeft != null && e && e.activeLabel != null) setRefRight(e.activeLabel); };
  const onUp = () => {
    if (refLeft != null && refRight != null && refLeft !== refRight) {
      const [a, b] = [refLeft, refRight].sort((x, y) => x - y);
      setDomain([a, b]);
    }
    setRefLeft(null);
    setRefRight(null);
  };

  const onSelectRange = (v) => {
    setDomain(null);
    if (v === "custom") {
      setRangeMode("custom");
      if (!fromVal) {
        const now = new Date();
        const yst = new Date(now.getTime() - 24 * 3600 * 1000);
        setFromVal(toLocalInput(yst));
        setToVal(toLocalInput(now));
      }
    } else {
      setRangeMode("preset");
      setHours(Number(v));
    }
  };

  const queryCustom = () => {
    if (!fromVal || !toVal) return;
    setApplied({ from: new Date(fromVal).toISOString(), to: new Date(toVal).toISOString() });
    setDomain(null);
  };

  const changeColor = (c) => { setChartColor(c); if (station) localStorage.setItem(`dc_color_${station.code}_${channel}`, c); };

  const yDomain = (yMin !== "" && yMax !== "") ? [Number(yMin), Number(yMax)] : ["auto", "auto"];

  const exportCsv = () => {
    let lines;
    let fname;
    if (compareMode && selectedChannels.length > 0) {
      const header = ["timestamp", ...selectedChannels.map((ch) => {
        const info = chInfo(ch);
        return `${info?.name || `ch${ch}`}${info?.unit ? ` (${info.unit})` : ""}`;
      })];
      lines = [header.join(",")];
      multiReadings.forEach((r) => {
        lines.push([new Date(r.t).toISOString(), ...selectedChannels.map((ch) => r[`ch_${ch}`] ?? "")].join(","));
      });
      fname = `${station?.code || "estacion"}_comparacion_${Date.now()}.csv`;
    } else {
      const header = ["timestamp", currentCh?.name || "value", currentCh?.unit || ""];
      lines = [header.join(",")];
      readings.forEach((r) => lines.push([r.timestamp, r.value, currentCh?.unit || ""].join(",")));
      fname = `${station?.code || "estacion"}_${currentCh?.field || "canal"}_${Date.now()}.csv`;
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const saveThresholds = async () => {
    try {
      await api.put(`/stations/${id}`, {
        name: metaForm.name,
        location: metaForm.location,
        lat: metaForm.lat === "" ? null : parseFloat(metaForm.lat),
        lng: metaForm.lng === "" ? null : parseFloat(metaForm.lng),
        channels,
      });
      toast.success(t("detail.saved"));
      loadStation();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveLocation = async () => {
    try {
      await api.put(`/stations/${id}`, {
        name: metaForm.name,
        location: metaForm.location,
        lat: metaForm.lat === "" ? null : parseFloat(metaForm.lat),
        lng: metaForm.lng === "" ? null : parseFloat(metaForm.lng),
      });
      toast.success(t("detail.saved"));
      loadStation();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateCh = (idx, field, val) => {
    setChannels(channels.map((c) => c.index === idx
      ? { ...c, [field]: val === "" ? null : parseFloat(val) } : c));
  };

  const updateChText = (idx, field, val) => {
    setChannels(channels.map((c) => c.index === idx ? { ...c, [field]: val } : c));
  };

  const sendMessage = async () => {
    if (!msg.message.trim()) return;
    try {
      await api.post("/messages", { station_id: id, message: msg.message, sms: msg.sms });
      toast.success(t("detail.messageSent"));
      setMsg({ message: "", sms: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fmtVal = (r) => {
    if (!r) return "—";
    if (r.type === "bool") return r.value >= 0.5 ? "ON" : "OFF";
    return r.value;
  };

  const RangeControls = ({ withChannel = true }) => (
    <div className="flex flex-wrap items-end gap-3">
      {withChannel && (
        <Select value={String(channel)} onValueChange={(v) => setChannel(Number(v))}>
          <SelectTrigger className="w-52" data-testid="channel-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {channels.map((c) => <SelectItem key={c.index} value={String(c.index)}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Select value={rangeMode === "custom" ? "custom" : String(hours)} onValueChange={onSelectRange}>
        <SelectTrigger className="w-48" data-testid="range-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="1">{t("detail.lastHour")}</SelectItem>
          <SelectItem value="6">{t("detail.last6h")}</SelectItem>
          <SelectItem value="12">{t("detail.last12h")}</SelectItem>
          <SelectItem value="24">{t("detail.last24h")}</SelectItem>
          <SelectItem value="72">{t("detail.last3d")}</SelectItem>
          <SelectItem value="168">{t("detail.last7d")}</SelectItem>
          <SelectItem value="custom">{t("detail.custom")}</SelectItem>
        </SelectContent>
      </Select>
      {rangeMode === "custom" && (
        <>
          <div>
            <Label className="text-xs text-slate-500">{t("detail.from")}</Label>
            <Input type="datetime-local" data-testid="custom-from" value={fromVal}
              onChange={(e) => setFromVal(e.target.value)} className="mt-1 w-48 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">{t("detail.to")}</Label>
            <Input type="datetime-local" data-testid="custom-to" value={toVal}
              onChange={(e) => setToVal(e.target.value)} className="mt-1 w-48 font-mono text-xs" />
          </div>
          <Button onClick={queryCustom} data-testid="query-custom" className="bg-blue-600 text-white hover:bg-blue-700">
            <Search className="mr-2 h-4 w-4" /> {t("detail.query")}
          </Button>
        </>
      )}
    </div>
  );

  if (!station) return <Layout><div className="p-6 text-slate-400">{t("common.loading")}</div></Layout>;

  return (
    <Layout>
      <PageHeader
        title={station.name}
        breadcrumb={[
          <button key="b" onClick={() => navigate("/stations")} className="hover:text-slate-600">{t("nav.stations")}</button>,
          station.code,
        ]}
        subtitle={station.location || station.code}
        actions={<><StatusBadge status={station.status} />
          <Button variant="outline" onClick={() => navigate("/stations")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}
          </Button></>}
      />
      <div className="space-y-6 p-6">
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{t("detail.realTimeValues")}</h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {channels.map((c) => (
              <Card key={c.index} data-testid={`live-ch-${c.index}`}
                onClick={() => setChannel(c.index)}
                className={`cursor-pointer border-slate-200 p-4 shadow-none transition-colors hover:border-blue-300 ${channel === c.index ? "border-blue-400 bg-blue-50/40" : ""}`}>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Droplets className="h-3.5 w-3.5 text-blue-500" strokeWidth={1.75} /> {c.name}
                </div>
                <div className="mt-2 font-mono text-2xl font-light text-slate-900">
                  {fmtVal(latest[c.index])}
                  <span className="ml-1 text-xs text-slate-400">{c.unit}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <Tabs defaultValue="chart">
          <TabsList>
            <TabsTrigger value="chart" data-testid="tab-chart">{t("detail.chart")}</TabsTrigger>
            <TabsTrigger value="table" data-testid="tab-table">{t("detail.detailedData")}</TabsTrigger>
            {canConfig && <TabsTrigger value="config" data-testid="tab-config">{t("detail.config")}</TabsTrigger>}
            <TabsTrigger value="message" data-testid="tab-message">{t("detail.sendMessage")}</TabsTrigger>
          </TabsList>

          <TabsContent value="chart">
            <Card className="border-slate-200 p-5 shadow-none">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <RangeControls withChannel={!compareMode} />
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2" data-testid="compare-toggle-wrap">
                    <Switch checked={compareMode} onCheckedChange={toggleCompare} data-testid="compare-toggle" />
                    <span className="text-sm text-slate-600">{t("detail.compareChannels")}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv">
                    <Download className="mr-2 h-4 w-4" /> {t("detail.exportCsv")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setDomain(null)} disabled={!domain}
                    data-testid="reset-zoom">
                    <ZoomOut className="mr-2 h-4 w-4" /> {t("detail.resetZoom")}
                  </Button>
                </div>
              </div>
              {compareMode ? (
                <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-slate-200 px-3 py-2" data-testid="compare-controls">
                  <span className="text-xs font-medium text-slate-500">{t("detail.selectChannels")}</span>
                  {channels.map((c) => (
                    <label key={c.index} data-testid={`compare-ch-${c.index}`}
                      className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-600">
                      <Checkbox checked={selectedChannels.includes(c.index)}
                        onCheckedChange={() => toggleChannelSel(c.index)} />
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: chColor(c.index) }} />
                      {c.name}{c.unit ? ` (${c.unit})` : ""}
                    </label>
                  ))}
                  <div className="ml-auto flex items-center gap-2">
                    <Switch checked={normalize} onCheckedChange={setNormalize} data-testid="normalize-toggle" />
                    <span className="text-sm text-slate-600">{t("detail.normalize")}</span>
                  </div>
                </div>
              ) : (
              <div className="mb-3 flex flex-wrap items-center gap-4 rounded-md border border-slate-200 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500">{t("detail.color")}</span>
                  {CHART_COLORS.map((c) => (
                    <button key={c} type="button" data-testid={`color-${c}`} onClick={() => changeColor(c)}
                      className={`h-5 w-5 rounded-full border-2 transition-transform hover:scale-110 ${chartColor === c ? "border-slate-900 dark:border-white" : "border-transparent"}`}
                      style={{ background: c }} aria-label={c} />
                  ))}
                  <input type="color" value={chartColor} onChange={(e) => changeColor(e.target.value)}
                    data-testid="color-picker" className="h-6 w-8 cursor-pointer rounded border border-slate-200 bg-transparent p-0" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500">{t("detail.yRange")}</span>
                  <Input type="number" data-testid="y-min" value={yMin} onChange={(e) => setYMin(e.target.value)}
                    placeholder={t("detail.min")} className="h-8 w-24 font-mono text-xs" />
                  <span className="text-slate-400">—</span>
                  <Input type="number" data-testid="y-max" value={yMax} onChange={(e) => setYMax(e.target.value)}
                    placeholder={t("detail.max")} className="h-8 w-24 font-mono text-xs" />
                  {(yMin !== "" || yMax !== "") && (
                    <button type="button" data-testid="y-auto" onClick={() => { setYMin(""); setYMax(""); }}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700">Auto</button>
                  )}
                </div>
              </div>
              )}
              <p className="mb-2 text-xs text-slate-400">{compareMode ? t("detail.compareHint") : t("detail.dragZoom")}</p>
              <div className="h-80 select-none" data-testid="readings-chart">
                <ResponsiveContainer width="100%" height="100%">
                  {compareMode ? (
                    <LineChart data={compareData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                      onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="t" type="number" scale="time" allowDataOverflow
                        domain={domain || ["dataMin", "dataMax"]} tickFormatter={fmtTick}
                        tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={40} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} width={45}
                        domain={normalize ? [0, 100] : yDomain} allowDataOverflow
                        tickFormatter={normalize ? (v) => `${v}%` : undefined} />
                      <Tooltip labelFormatter={fmtFull}
                        formatter={(v, name) => {
                          const ch = Number(String(name).replace("ch_", ""));
                          const info = chInfo(ch);
                          const label = info?.name || name;
                          return normalize
                            ? [`${Number(v).toFixed(1)}%`, label]
                            : [`${v} ${info?.unit || ""}`, label];
                        }}
                        contentStyle={{ fontSize: 12, borderRadius: 8, background: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))", color: "hsl(var(--popover-foreground))" }} />
                      <Legend formatter={(name) => {
                        const ch = Number(String(name).replace("ch_", ""));
                        const info = chInfo(ch);
                        return info ? `${info.name}${info.unit ? ` (${info.unit})` : ""}` : name;
                      }} wrapperStyle={{ fontSize: 12 }} />
                      {selectedChannels.map((ch) => (
                        <Line key={ch} type="monotone" dataKey={`ch_${ch}`} stroke={chColor(ch)} strokeWidth={2}
                          isAnimationActive={false} dot={false} connectNulls />
                      ))}
                      {refLeft != null && refRight != null && (
                        <ReferenceArea x1={refLeft} x2={refRight} strokeOpacity={0.3} fill="#2563eb" fillOpacity={0.12} />
                      )}
                      <Brush dataKey="t" height={26} stroke="#94a3b8" travellerWidth={8} tickFormatter={fmtTick} y={296} />
                    </LineChart>
                  ) : (
                  <AreaChart data={readings} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                    onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}>
                    <defs>
                      <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartColor} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="t" type="number" scale="time" allowDataOverflow
                      domain={domain || ["dataMin", "dataMax"]} tickFormatter={fmtTick}
                      tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={40} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} width={45} domain={yDomain} allowDataOverflow />
                    <Tooltip labelFormatter={fmtFull}
                      formatter={(v) => [`${v} ${currentCh?.unit || ""}`, currentCh?.name || t("detail.value")]}
                      contentStyle={{ fontSize: 12, borderRadius: 8, background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))", color: "hsl(var(--popover-foreground))" }} />
                    {currentCh?.min_threshold != null && (
                      <ReferenceLine y={currentCh.min_threshold} stroke="#f59e0b" strokeDasharray="4 4"
                        label={{ value: t("detail.min"), position: "insideLeft", fontSize: 10, fill: "#f59e0b" }} />
                    )}
                    {currentCh?.max_threshold != null && (
                      <ReferenceLine y={currentCh.max_threshold} stroke="#ef4444" strokeDasharray="4 4"
                        label={{ value: t("detail.max"), position: "insideLeft", fontSize: 10, fill: "#ef4444" }} />
                    )}
                    <Area type="monotone" dataKey="value" stroke={chartColor} strokeWidth={2} fill="url(#fill)"
                      isAnimationActive={false} dot={false} />
                    {refLeft != null && refRight != null && (
                      <ReferenceArea x1={refLeft} x2={refRight} strokeOpacity={0.3} fill={chartColor} fillOpacity={0.12} />
                    )}
                    <Brush dataKey="t" height={26} stroke={chartColor} travellerWidth={8} tickFormatter={fmtTick} y={296} />
                  </AreaChart>
                  )}
                </ResponsiveContainer>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="table">
            <Card className="border-slate-200 shadow-none">
              <div className="border-b border-slate-100 p-4">
                <RangeControls />
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                <Table data-testid="readings-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("detail.date")}</TableHead>
                      <TableHead>{t("detail.channel")}</TableHead>
                      <TableHead>{t("detail.parameter")}</TableHead>
                      <TableHead className="text-right">{t("detail.value")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...readings].reverse().slice(0, 500).map((r) => (
                      <TableRow key={`${r.t}-${r.value}`}>
                        <TableCell className="font-mono text-xs text-slate-500">{new Date(r.timestamp).toLocaleString()}</TableCell>
                        <TableCell className="text-sm">{r.channel}</TableCell>
                        <TableCell className="text-sm">{r.channel_name}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtVal(r)} {r.unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {canConfig && (
            <TabsContent value="config">
              <Card className="border-slate-200 p-5 shadow-none">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-900">{t("detail.thresholdsTitle")}</h3>
                  <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={saveThresholds} data-testid="save-thresholds">
                    <Save className="mr-2 h-4 w-4" /> {t("common.save")}
                  </Button>
                </div>
                <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 p-4">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("detail.stationInfo")}</div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500">{t("common.name")}</Label>
                        <Input data-testid="meta-name" value={metaForm.name}
                          onChange={(e) => setMetaForm({ ...metaForm, name: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{t("common.location")}</Label>
                        <Input data-testid="meta-location" value={metaForm.location}
                          onChange={(e) => setMetaForm({ ...metaForm, location: e.target.value })} className="mt-1" />
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-4">
                    <div className="mb-1 flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-50">
                        <MapPin className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
                      </div>
                      <span className="text-sm font-semibold text-slate-800">{t("detail.locationTitle")}</span>
                    </div>
                    <p className="mb-3 text-xs text-slate-400">{t("detail.locationHint")}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">{t("detail.latitude")}</Label>
                        <Input type="number" step="any" data-testid="meta-lat" value={metaForm.lat}
                          onChange={(e) => setMetaForm({ ...metaForm, lat: e.target.value })} className="mt-1 font-mono" placeholder="41.38" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{t("detail.longitude")}</Label>
                        <Input type="number" step="any" data-testid="meta-lng" value={metaForm.lng}
                          onChange={(e) => setMetaForm({ ...metaForm, lng: e.target.value })} className="mt-1 font-mono" placeholder="2.17" />
                      </div>
                    </div>
                    <Button size="sm" onClick={saveLocation} data-testid="save-location"
                      className="mt-3 bg-blue-600 text-white hover:bg-blue-700">
                      <MapPin className="mr-2 h-4 w-4" /> {t("detail.saveLocation")}
                    </Button>
                  </div>
                </div>
                <div className="space-y-3">
                  {channels.map((c) => (
                    <div key={c.index} className="grid grid-cols-1 items-end gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-5">
                      <div>
                        <Label className="text-xs text-slate-500">{t("detail.label")}</Label>
                        <Input data-testid={`label-${c.index}`} value={c.name}
                          onChange={(e) => updateChText(c.index, "name", e.target.value)} className="mt-1" />
                        <div className="mt-1 font-mono text-[10px] uppercase text-slate-400">{c.field}</div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{t("detail.unit")}</Label>
                        <Input data-testid={`unit-${c.index}`} value={c.unit || ""}
                          onChange={(e) => updateChText(c.index, "unit", e.target.value)} className="mt-1" placeholder="°C" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{t("detail.min")}</Label>
                        <Input type="number" data-testid={`min-${c.index}`}
                          value={c.min_threshold ?? ""} onChange={(e) => updateCh(c.index, "min_threshold", e.target.value)}
                          className="mt-1 font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{t("detail.max")}</Label>
                        <Input type="number" data-testid={`max-${c.index}`}
                          value={c.max_threshold ?? ""} onChange={(e) => updateCh(c.index, "max_threshold", e.target.value)}
                          className="mt-1 font-mono" />
                      </div>
                      <div className="text-xs text-slate-400">{t("detail.generatesAlarm")}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="message">
            <Card className="border-slate-200 p-5 shadow-none max-w-xl">
              <h3 className="mb-4 text-base font-semibold text-slate-900">{t("detail.messageTitle")}</h3>
              <div className="space-y-4">
                <div>
                  <Label>{t("detail.message")}</Label>
                  <Textarea data-testid="message-input" value={msg.message}
                    onChange={(e) => setMsg({ ...msg, message: e.target.value })}
                    className="mt-1.5" rows={4} placeholder={t("detail.messagePlaceholder")} />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={msg.sms} onCheckedChange={(v) => setMsg({ ...msg, sms: v })} data-testid="sms-switch" />
                  <span className="text-sm text-slate-600">{t("detail.alsoSms")}</span>
                </div>
                <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={sendMessage} data-testid="send-message-btn">
                  <Send className="mr-2 h-4 w-4" /> {t("detail.sendMessage")}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
